import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';

import type { Campaign } from '@feathr/blackbox';
import { EGoalType, EGoalTypeV1, type Goal as GoalModel } from '@feathr/blackbox';

import ConversionPixelTriggerGoal from './ConversionPixelTriggerGoal';
import DonationFormSubmissionGoal from './DonationFormSubmissionGoal';
import FormSubmissionGoal from './FormSubmissionGoal';
import GoalLayout from './GoalLayout';
import GoalV1 from './GoalV1';
import GroupEntryGoal from './GroupEntryGoal';
import URLVisitGoal from './URLVisitGoal';

interface IProps {
  goal: GoalModel;
  campaign?: Campaign;
}

function getContent(goal: GoalModel, isAdvancedConversionTracking: boolean): ReactNode {
  switch (goal.get('conv_type')) {
    case EGoalType.page_view:
      return <URLVisitGoal goal={goal} />;

    case EGoalType.form_submission:
      return <FormSubmissionGoal goal={goal} />;

    case EGoalType.donation_form_submission:
      return <DonationFormSubmissionGoal goal={goal} />;

    case EGoalType.group_entry:
      return <GroupEntryGoal goal={goal} />;

    case EGoalType.conversion_pixel:
      return <ConversionPixelTriggerGoal goal={goal} />;

    case EGoalTypeV1.custom: {
      if (isAdvancedConversionTracking) {
        return <ConversionPixelTriggerGoal goal={goal} />;
      }
      return <GroupEntryGoal goal={goal} />;
    }

    default:
      return null;
  }
}

function Goal({ goal, campaign }: Readonly<IProps>): JSX.Element {
  const isAdvancedConversionTracking = campaign?.get('conversion_tracking_mode') === 'advanced';
  return <GoalLayout goal={goal}>{getContent(goal, isAdvancedConversionTracking)}</GoalLayout>;
}

Goal.V1 = GoalV1;

export default observer(Goal);
